import { defineStore, getActivePinia } from "pinia";
import { computed, ref } from "vue";
import router from "@/router";
import { api } from "@/api";
import { notify } from "@kyvg/vue3-notification";
import i18n from "@/i18n";
import {
  parseAccessToken,
  setCookie,
  setUserLogin,
  clearUserLogin,
  getCookie,
} from "@/helpers";

export const useAuthStore = defineStore("auth.session", () => {
  const version = ref(0);
  let sessionVersion = 0;

  const AUTH_EVENT_KEY = "auth:event";

  function emitAuthEvent() {
    try {
      localStorage.setItem(
        AUTH_EVENT_KEY,
        crypto.getRandomValues(new Uint32Array(4)).join("-"),
      );
    } catch {}
  }

  function readAuthEvent(): string | null {
    try {
      return localStorage.getItem(AUTH_EVENT_KEY);
    } catch {
      return null;
    }
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
      if (e.key !== AUTH_EVENT_KEY) return;
      bump();
      resetAllStores();
    });
  }

  const token = computed(() => {
    version.value;
    return parseAccessToken();
  });

  const roles = computed(() => token.value?.roles ?? []);

  function bump() {
    version.value += 1;
  }

  function setTokens(payload: { access: string; refresh: string }) {
    sessionVersion += 1;
    setCookie("access_token", payload.access, 7);
    setCookie("refresh_token", payload.refresh, 30);
    bump();
    emitAuthEvent();
  }

  function clearTokens() {
    setCookie("access_token", "", -1);
    setCookie("refresh_token", "", -1);
    bump();
    emitAuthEvent();
  }

  function onLogin(payload: {
    access: string;
    refresh: string;
    login: string;
  }) {
    setTokens({ access: payload.access, refresh: payload.refresh });
    setUserLogin(payload.login);
  }

  function resetAllStores() {
    const pinia = getActivePinia() as any;
    const storesMap: Map<string, any> | undefined = pinia?._s;
    if (!storesMap) return;

    for (const [id, store] of storesMap.entries()) {
      if (id === "auth.session") continue;
      if (typeof store?.reset === "function") {
        store.reset();
        continue;
      }
      if (typeof store?.$reset === "function") {
        store.$reset();
      }
    }
  }

  let logoutInFlight: Promise<boolean> | null = null;

  function logout(options?: { redirectToLogin?: boolean }): Promise<boolean> {
    if (logoutInFlight) return logoutInFlight;
    logoutInFlight = (async () => {
      const logoutSessionVersion = sessionVersion;
      const logoutAuthEvent = readAuthEvent();
      const refresh = getCookie("refresh_token");
      const access = getCookie("access_token");
      try {
        const credential = refresh || access;
        if (credential)
          await api.logout(
            credential,
            refresh ? (access ?? undefined) : undefined,
          );
        const currentRefresh = getCookie("refresh_token");
        const currentAccess = getCookie("access_token");
        if (
          (currentRefresh || currentAccess) &&
          (sessionVersion !== logoutSessionVersion ||
            readAuthEvent() !== logoutAuthEvent)
        )
          return true;
        clearTokens();
        clearUserLogin();
        resetAllStores();
        if (options?.redirectToLogin !== false) {
          await router.push("/login");
        }
        return true;
      } catch (error) {
        console.error("Session logout failed", error);
        notify({ type: "error", text: i18n.global.t("auth.logout_failed") });
        return false;
      }
    })().finally(() => {
      logoutInFlight = null;
    });
    return logoutInFlight;
  }

  return {
    token,
    roles,
    bump,
    setTokens,
    clearTokens,
    onLogin,
    logout,
  };
});
